import React, { useEffect, useState } from "react";
import {
  Grid, Paper, Typography, Divider, Box, Chip, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Button,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HotelIcon from "@mui/icons-material/Hotel";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import { instance } from "../../axios";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getPatientAction } from "../../redux/actions/patient";
import { getVisitsAction, createVisitAction, cleanVisitAction } from "../../redux/actions/visit";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const genderIcon = (patient) => {
  const age = patient.birthDate ? dayjs().diff(dayjs(patient.birthDate), "year") : null;
  if (age !== null && age < 18) return <ChildCareIcon sx={{ mr: 1 }} />;
  return patient.gender === "man" ? <ManIcon sx={{ mr: 1 }} /> : <WomanIcon sx={{ mr: 1 }} />;
};

const visitTypeLabel = (type) =>
  type === "evaluacion" ? "Consulta médica ocupacional" : "Consulta general";


const InfoRow = ({ label, value }) =>
  value ? (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  ) : null;

const PatientDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patient = useSelector((state) => state.patient.patient);
  const visits = useSelector((state) => state.visit.visits);
  const user = useSelector((state) => state.user.user);

  const [openDialog, setOpenDialog] = useState(false);
  const [visitType, setVisitType] = useState("evaluacion");
  const [visitDate, setVisitDate] = useState(dayjs());
  const [creating, setCreating] = useState(false);
  const [hoveredVisit, setHoveredVisit] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [page, setPage] = useState(0);
  const ROWS_PER_PAGE = 5;

  const handleDownloadPdf = async (filename, key, e) => {
    e.stopPropagation();
    if (!filename || downloadingPdf === key) return;
    setDownloadingPdf(key);
    try {
      const dlRes = await instance.get(`/visit/constancia/${filename}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([dlRes.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading PDF:", err);
    } finally {
      setDownloadingPdf(null);
    }
  };

  useEffect(() => {
    dispatch(getPatientAction(id));
    dispatch(getVisitsAction(id));
    return () => dispatch(cleanVisitAction());
  }, [id, dispatch]);

  const handleCreateVisit = async () => {
    setCreating(true);
    try {
      const created = await dispatch(
        createVisitAction({
          patientId: parseInt(id),
          userId: user.id,
          type: visitType,
          visitDate: visitDate.toISOString(),
        })
      );
      navigate(`/visits/${created.id}`);
    } catch (err) {
      console.error("Error creating visit:", err);
    } finally {
      setCreating(false);
    }
  };

  if (!patient) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const subscriptionLabel = (ps) => {
    const type = ps.Subscription?.type;
    if (type === "individual") return "Individual";
    if (type === "family") return patient.parent_id ? `Familiar (${patient.Parent?.displayName || ""})` : "Jefe de familia";
    if (type === "company") return `Empresa (${ps.Subscription?.Company?.name || ""})`;
    return type;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate("/patients")} sx={{ alignSelf: "flex-start", mb: 1, color: "text.secondary" }}>
        Volver
      </Button>

      <Grid container spacing={2} sx={{ flex: 1, alignItems: "stretch" }}>

        {/* ── Left: patient info ─────────────────────────────────── */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Paper sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            <Box display="flex" alignItems="center" mb={1}>
              {genderIcon(patient)}
              <Typography variant="h6">{patient.displayName}</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <InfoRow label="Cédula" value={patient.identityNumber} />
            <InfoRow
              label="Fecha de nacimiento"
              value={
                patient.birthDate
                  ? `${dayjs(patient.birthDate).format("DD/MM/YYYY")} (${dayjs().diff(dayjs(patient.birthDate), "year")} años)`
                  : null
              }
            />
            <InfoRow label="Grupo sanguíneo" value={patient.bloodType} />
            <InfoRow label="Teléfono" value={patient.phone} />
            <InfoRow label="Dirección" value={patient.address} />
            <InfoRow label="Correo" value={patient.mail} />
            <InfoRow label="Comentario" value={patient.comment} />

            {(patient.estadoCivil || patient.nacionalidad || patient.lugarNacimiento ||
              patient.parroquia || patient.municipio || patient.estadoResidencia ||
              patient.nivelEducativo || patient.manoDominante) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Datos personales</Typography>
                <InfoRow label="Estado civil" value={
                  patient.estadoCivil
                    ? { soltero: "Soltero/a", casado: "Casado/a", divorciado: "Divorciado/a", viudo: "Viudo/a", concubinato: "Concubinato" }[patient.estadoCivil]
                    : null
                } />
                <InfoRow label="Nacionalidad" value={patient.nacionalidad} />
                <InfoRow label="Lugar de nacimiento" value={patient.lugarNacimiento} />
                <InfoRow label="Parroquia" value={patient.parroquia} />
                <InfoRow label="Municipio" value={patient.municipio} />
                <InfoRow label="Estado" value={patient.estadoResidencia} />
                <InfoRow label="Nivel educativo" value={
                  patient.nivelEducativo
                    ? { iletrado: "Iletrado", primaria: "Primaria", tecnica: "Técnica", secundaria: "Secundaria", universitaria: "Universitaria" }[patient.nivelEducativo]
                    : null
                } />
                <InfoRow label="Mano dominante" value={
                  patient.manoDominante
                    ? patient.manoDominante.charAt(0).toUpperCase() + patient.manoDominante.slice(1)
                    : null
                } />
              </>
            )}

            {(() => {
              const mods = patient.modules;
              const modules = Array.isArray(mods) ? mods : (typeof mods === "string" ? (() => { try { return JSON.parse(mods); } catch { return []; } })() : []);
              return (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Módulos</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                    {modules.length > 0
                      ? modules.map((mod) => <Chip key={mod} label={mod} size="small" color="primary" variant="outlined" />)
                      : <Typography variant="body2" color="text.secondary">—</Typography>
                    }
                  </Box>
                </>
              );
            })()}

            {patient.PatientSubscriptions?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">Suscripciones</Typography>
                {patient.PatientSubscriptions.map((ps) => (
                  <Box key={ps.subscriptionId} sx={{ mt: 0.5 }}>
                    <Chip label={subscriptionLabel(ps)} size="small" variant="outlined" />
                    {ps.start && (
                      <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                        desde {dayjs(ps.start).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}

            {user.type !== "viewer" && (
              <Box display="flex" justifyContent="flex-end" mt="auto" pt={2}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    dispatch(getPatientAction(patient.id));
                    navigate("/patients/edit");
                  }}
                >
                  Editar
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Right: visits + documents ──────────────────────────── */}
        <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column" }}>

          {/* Visitas */}
          <Paper sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Visitas</Typography>
            <Divider sx={{ mb: 2 }} />

            {!visits || visits.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" my={2}>
                Sin visitas registradas.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Médico</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}><Tooltip title="Evaluación" arrow><AssignmentTurnedInIcon fontSize="small" color="inherit" /></Tooltip></TableCell>
                    <TableCell sx={{ color: "text.secondary" }}><Tooltip title="Reposo médico" arrow><HotelIcon fontSize="small" color="inherit" /></Tooltip></TableCell>
                    <TableCell sx={{ color: "text.secondary" }}><Tooltip title="Histórico" arrow><HistoryEduIcon fontSize="small" color="inherit" /></Tooltip></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visits.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE).map((visit) => {
                    const statusMap = {
                      pending:      { label: "En curso",        color: "default" },
                      completed:    { label: "Evaluada",        color: "primary" },
                      waiting_sign: { label: "Pendiente firma", color: "warning" },
                      signed:       { label: "Firmada",         color: "success" },
                    };
                    const s = statusMap[visit.status] || { label: visit.status, color: "default" };
                    return (
                      <React.Fragment key={visit.id}>
                        <TableRow
                          sx={{
                            cursor: "pointer",
                            bgcolor: hoveredVisit === visit.id ? "action.hover" : "transparent",
                            "& td": { borderBottom: "none" },
                          }}
                          onMouseEnter={() => setHoveredVisit(visit.id)}
                          onMouseLeave={() => setHoveredVisit(null)}
                          onClick={() => navigate(`/visits/${visit.id}`)}
                        >
                          <TableCell>{dayjs(visit.visitDate).format("DD/MM/YYYY HH:mm")}</TableCell>
                          <TableCell>{visitTypeLabel(visit.type)}</TableCell>
                          <TableCell>{visit.User?.displayName || "—"}</TableCell>
                          <TableCell><Chip label={s.label} color={s.color} size="small" /></TableCell>
                          <TableCell
                            sx={{ color: visit.evaluacionFilename ? "primary.main" : "text.disabled", cursor: visit.evaluacionFilename ? "pointer" : "default" }}
                            onClick={(e) => handleDownloadPdf(visit.evaluacionFilename, `eval-${visit.id}`, e)}
                          >
                            <AssignmentTurnedInIcon fontSize="small" />
                          </TableCell>
                          <TableCell
                            sx={{ color: visit.reposoMedicoFilename ? "primary.main" : "text.disabled", cursor: visit.reposoMedicoFilename ? "pointer" : "default" }}
                            onClick={(e) => handleDownloadPdf(visit.reposoMedicoFilename, `reposo-${visit.id}`, e)}
                          >
                            <HotelIcon fontSize="small" />
                          </TableCell>
                          <TableCell
                            sx={{ color: visit.historicoFilename ? "primary.main" : "text.disabled", cursor: visit.historicoFilename ? "pointer" : "default" }}
                            onClick={(e) => handleDownloadPdf(visit.historicoFilename, `historico-${visit.id}`, e)}
                          >
                            <HistoryEduIcon fontSize="small" />
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            cursor: "pointer",
                            bgcolor: hoveredVisit === visit.id ? "action.hover" : "transparent",
                          }}
                          onMouseEnter={() => setHoveredVisit(visit.id)}
                          onMouseLeave={() => setHoveredVisit(null)}
                          onClick={() => navigate(`/visits/${visit.id}`)}
                        >
                          <TableCell colSpan={7} sx={{ pt: 0, pb: 0.75 }}>
                            <Typography variant="caption" color={visit.motivoConsulta ? "text.secondary" : "text.disabled"} display="block">
                              <strong>Motivo: </strong>{visit.motivoConsulta || "Sin motivo"}
                            </Typography>
                            <Typography variant="caption" color={visit.diagnostico ? "text.secondary" : "text.disabled"} display="block">
                              <strong>Diagnóstico: </strong>{(() => { try { const p = JSON.parse(visit.diagnostico); return Array.isArray(p) ? p.map(d => d.label || d.code).join(", ") : visit.diagnostico; } catch { return visit.diagnostico || "Sin diagnóstico"; } })()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {visits && visits.length > ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={visits.length}
                page={page}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                onPageChange={(_, newPage) => setPage(newPage)}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            )}

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setOpenDialog(true)}
              >
                Nueva visita
              </Button>
            </Box>
          </Paper>

        </Grid>

      </Grid>

      {/* ── Dialog: nueva visita ───────────────────────────────── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nueva visita — {patient.displayName}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Tipo de visita</InputLabel>
            <Select
              value={visitType}
              label="Tipo de visita"
              onChange={(e) => setVisitType(e.target.value)}
            >
              <MenuItem value="consulta" disabled>Consulta general</MenuItem>
              <MenuItem value="evaluacion">Consulta médica ocupacional</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha de la visita"
              value={visitDate}
              onChange={setVisitDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateVisit} disabled={creating}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDetail;
